// src/pages/duelIdentity.js
// Shared anonymous-play identity for Arena (spec section 11). A visitor
// with no token gets a random anon_id in localStorage the first time any
// Arena page needs one; every duel API call sends it via the X-Anon-Id
// header as a fallback identifier. Real login/signup always wins - a
// token in localStorage means anon_id is simply never read/sent.
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// Display label only - the underlying duel `mode` value is still the literal
// string 'async' everywhere (DB/API/backend all use it, and changing it
// there would break every existing duel row) - this just renames what a
// user actually sees for it, wherever that mode gets shown as text.
export const DUEL_MODE_LABEL = { live: 'Live', async: 'Marathon', open: 'Open' };

export function isLoggedIn() {
  return Boolean(localStorage.getItem('token'));
}

// crypto.randomUUID only exists on secure origins (https, localhost) and
// newer browsers (Safari 15.4+). The backend stores anon_id as a uuid, so
// the fallback must still be a valid RFC 4122 v4 string.
function uuidV4Fallback() {
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function getOrCreateAnonId() {
  if (isLoggedIn()) return null;
  let id = localStorage.getItem('anon_id');
  // Older builds stored a non-UUID fallback that the backend rejects;
  // nothing could be saved under it, so replace it with a fresh one.
  if (id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) id = null;
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : uuidV4Fallback();
    localStorage.setItem('anon_id', id);
  }
  return id;
}

// hasIdentity() always returns true after the first call to
// getOrCreateAnonId() in a session (token or freshly-minted anon_id) -
// pages call this once on mount to decide whether there's anyone to load
// data for at all, vs. the true edge case of localStorage being blocked.
export function hasIdentity() {
  return isLoggedIn() || Boolean(getOrCreateAnonId());
}

// Drop-in replacement for the apiFetch() every Arena page previously
// defined locally - sends a real bearer token when logged in, otherwise
// falls back to X-Anon-Id. Routes that require a real account (creating a
// duel, posting to the open board) still just 401 for an anon-only
// caller - the frontend is responsible for showing the signup prompt at
// that point, this helper doesn't hide that distinction.
export function duelFetch(path, method, body) {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = 'Bearer ' + token;
  } else {
    headers['X-Anon-Id'] = getOrCreateAnonId();
  }
  return fetch(`${API_BASE}${path}`, {
    method: method || 'GET', headers,
    body: body ? JSON.stringify(body) : undefined,
  });
}

// Defensive unwrap for any endpoint expected to return a JSON array.
// app.py's own @app.errorhandler(404) returns a JSON *object*
// ({status:'error', message:'Endpoint not found'}), not an array, on a
// route that doesn't exist server-side - a plain `.json().catch(() => [])`
// doesn't help there, since JSON parsing SUCCEEDS (nothing for .catch to
// catch), it's just the wrong shape, and callers doing `.map()`/`.find()`
// on that object crash outright. Real for this codebase specifically:
// hit live after shipping a new duel_routes.py endpoint whose backend
// process hadn't restarted yet. Covers a genuine parse/network failure
// too, same as the .catch() it replaces.
export async function fetchJsonArray(res) {
  try {
    const json = await res.json();
    return Array.isArray(json) ? json : [];
  } catch (e) {
    return [];
  }
}

// Call right after a successful register/login when an anon_id existed -
// merges the guest's duel history/streak/rating/badges into the new
// account server-side (spec item 5), then clears the local anon_id since
// it's now folded into the real account and shouldn't be reused.
export async function claimAnonAfterAuth(token) {
  const anonId = localStorage.getItem('anon_id');
  if (!anonId) return;
  try {
    await fetch(`${API_BASE}/api/duels/claim-anon`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ anon_id: anonId }),
    });
  } catch (e) {
    // not fatal - worst case they keep separate (still-real) guest history
  }
  localStorage.removeItem('anon_id');
}
